import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  connect() {
    this.refresh()
  }

  refresh(event) {
    // A checkbox emits input before change. Keep the clicked master state intact
    // until bulk-selection has applied it to the position checkboxes.
    if (event?.target.matches("input[name='packing_select_all'], input[name='packing_item_ids[]']")) return

    if (event?.target.matches('[data-autocomplete-target="input"]')) {
      const packagingArticle = event.target.closest("[data-packing-package]")?.querySelector('[data-autocomplete-target="hidden"]')
      if (packagingArticle) packagingArticle.value = ""
    }
    const packages = Array.from(this.element.querySelectorAll("[data-packing-package]"))
    const format = new Intl.NumberFormat(document.documentElement.lang || "de", { maximumFractionDigits: 3, useGrouping: false })
    this.element.querySelectorAll("[data-packing-position]").forEach(row => {
      const badge = row.querySelector("[data-unpacked-quantity]")
      if (!badge) return
      const removed = row.querySelector("[data-packing-remove-item]")?.checked || false
      row.classList.toggle("table-danger", removed)
      const totalInput = row.querySelector("[data-packing-quantity]")
      if (totalInput) totalInput.disabled = removed
      packages.forEach(packageElement => {
        packageElement.querySelectorAll("[data-packing-content-id]").forEach(input => {
          if (input.dataset.packingContentId === row.dataset.packingPosition) {
            input.disabled = removed
            input.closest("tr").hidden = removed
          }
        })
      })
      const total = Number(totalInput ? totalInput.value : row.dataset.packingTotal)
      let packed = Number(row.dataset.packingPacked)
      if (packages.length) {
        packed = packages.reduce((sum, packageElement) => {
          if (packageElement.querySelector("[data-packing-remove]")?.checked) return sum
          const inputs = packageElement.querySelectorAll("[data-packing-content-id]")
          return sum + Array.from(inputs).filter(input => input.dataset.packingContentId === row.dataset.packingPosition)
            .reduce((quantity, input) => quantity + (Number(input.value) || 0), 0)
        }, 0)
      }
      // Match the precision of persisted quantities, including decimal splits.
      const remaining = Math.max(Math.round((total - packed) * 1000) / 1000, 0)
      const packedBadge = row.querySelector("[data-packed-quantity]")
      packedBadge.textContent = format.format(packed)
      packedBadge.classList.toggle("bg-danger", packed <= 0)
      packedBadge.classList.toggle("bg-warning", packed > 0 && remaining > 0)
      packedBadge.classList.toggle("text-dark", packed > 0 && remaining > 0)
      packedBadge.classList.toggle("bg-success", packed > 0 && remaining <= 0)
      badge.hidden = removed || remaining <= 0
      badge.querySelector("[data-unpacked-count]").textContent = format.format(remaining)
      const checkbox = row.querySelector("input[name='packing_item_ids[]']")
      if (checkbox) {
        checkbox.disabled = removed || remaining <= 0
        if (checkbox.disabled) checkbox.checked = false
      }
    })
    this.refreshWeights(packages)
    this.element.querySelectorAll('[data-controller~="bulk-selection"]').forEach(element => {
      this.application.getControllerForElementAndIdentifier(element, "bulk-selection")?.refreshMaster()
    })
  }
  packagingArticleChanged(event) {
    const packageElement = event.target.closest("[data-packing-package]")
    if (!packageElement) return
    const selected = event.detail.selected
    packageElement.dataset.packagingWeight = selected.dataset.partWeightKg || ""
    ;["length", "width", "height"].forEach(dimension => {
      const input = packageElement.querySelector(`[data-package-dimension="${dimension}"]`)
      const key = `partOuter${dimension[0].toUpperCase()}${dimension.slice(1)}`
      if (input) input.value = selected.dataset[key] || ""
    })
    this.refresh()
  }

  refreshWeights(packages) {
    const format = new Intl.NumberFormat(document.documentElement.lang || "de", { maximumFractionDigits: 3, useGrouping: false })
    const round = value => Math.round((value + Number.EPSILON) * 1000) / 1000
    const weights = new Map()
    let totalNet = 0
    this.element.querySelectorAll("[data-packing-position]").forEach(row => {
      const rawWeight = row.dataset.unitWeightKg
      const weight = rawWeight === undefined || rawWeight === "" ? null : Number(rawWeight)
      weights.set(row.dataset.packingPosition, weight)
      if (row.querySelector("[data-packing-remove-item]")?.checked) return
      const quantity = Number(row.querySelector("[data-packing-quantity]")?.value ?? row.dataset.packingTotal)
      if (quantity > 0) totalNet = totalNet !== null && weight !== null ? totalNet + quantity * weight : null
    })
    const effective = { net_weight: [], gross_weight: [] }
    let totalPackaging = 0
    packages.forEach(packageElement => {
      const net = packageElement.querySelector('[data-package-weight="net_weight"]')
      const gross = packageElement.querySelector('[data-package-weight="gross_weight"]')
      if (!net || !gross) return
      const status = this.element.querySelector('[name="deliverable[status]"]')?.value
      net.required = gross.required = ["shipped", "on_loan", "returned"].includes(status) && !packageElement.querySelector("[data-packing-remove]")?.checked
      let calculatedNet = 0
      packageElement.querySelectorAll("[data-packing-content-id]").forEach(input => {
        if (input.disabled || Number(input.value) <= 0) return
        const weight = weights.get(input.dataset.packingContentId) ?? null
        calculatedNet = calculatedNet !== null && weight !== null ? calculatedNet + Number(input.value) * weight : null
      })
      if (calculatedNet !== null) calculatedNet = round(calculatedNet)
      const packagingSelected = !!packageElement.querySelector('[data-autocomplete-target="hidden"]')?.value
      const rawPackagingWeight = packageElement.dataset.packagingWeight
      const tare = packagingSelected ? (rawPackagingWeight === "" || rawPackagingWeight === undefined ? null : Number(rawPackagingWeight)) : 0
      const calculatedGross = calculatedNet !== null && tare !== null ? round(calculatedNet + tare) : null
      const suggested = { net_weight: calculatedNet, gross_weight: calculatedGross }
      const actual = {}
      ;[net, gross].forEach(input => {
        const key = input.dataset.packageWeight
        input.placeholder = suggested[key] === null ? input.dataset.weightIncomplete : String(suggested[key])
        actual[key] = input.value === "" ? suggested[key] : Number(input.value)
        packageElement.querySelector(`[data-package-soll="${key}"]`).textContent = suggested[key] === null ? input.dataset.weightIncomplete : `${format.format(suggested[key])} kg`
      })
      gross.min = Math.max(actual.net_weight || 0, 0)
      gross.setCustomValidity(actual.net_weight !== null && actual.gross_weight !== null && actual.gross_weight < actual.net_weight ? gross.dataset.weightError : "")
      packageElement.querySelector("[data-packaging-weight-warning]").hidden = !packagingSelected || tare !== null
      if (packageElement.querySelector("[data-packing-remove]")?.checked) return
      if (totalPackaging !== null) totalPackaging = tare === null ? null : totalPackaging + tare
      Object.keys(effective).forEach(key => effective[key].push(actual[key]))
    })
    const totals = {
      suggested_net_weight: totalNet === null ? null : round(totalNet),
      suggested_gross_weight: totalNet === null || totalPackaging === null ? null : round(round(totalNet) + totalPackaging)
    }
    Object.keys(effective).forEach(key => {
      totals[key] = effective[key].length && effective[key].every(value => value !== null) ? effective[key].reduce((sum, value) => sum + value, 0) : null
    })
    this.element.querySelectorAll("[data-delivery-weight]").forEach(element => {
      const value = totals[element.dataset.deliveryWeight]
      element.textContent = value === null || value === undefined ? "— kg" : `${format.format(value)} kg`
    })
  }

}
